import type { Color, GameAction, GameState, Move, PieceType } from '@ccc/shared-types';
import type { Engine } from './engine.js';

export type BotDifficulty = 'beginner' | 'easy' | 'medium' | 'hard' | 'adaptive';

const VALUES: Record<PieceType, number> = {
  pawn: 100,
  'mini-rook': 150,
  knight: 320,
  bishop: 330,
  rook: 500,
  queen: 900,
  king: 20_000,
};

const SETTINGS: Record<BotDifficulty, { depth: number; noise: number; top: number; nodes: number }> = {
  beginner: { depth: 0, noise: 0, top: 999, nodes: 1 },
  easy: { depth: 0, noise: 90, top: 5, nodes: 80 },
  medium: { depth: 1, noise: 35, top: 8, nodes: 450 },
  hard: { depth: 2, noise: 8, top: 10, nodes: 1_500 },
  adaptive: { depth: 2, noise: 15, top: 10, nodes: 1_100 },
};

export function chooseBotMove(
  engine: Engine,
  state: GameState,
  side: Color,
  difficulty: BotDifficulty = 'beginner',
): Move | null {
  const action = chooseBotAction(engine, state, side, difficulty);
  return action?.kind === 'move' ? action.move : null;
}

export function chooseBotAction(
  engine: Engine,
  state: GameState,
  side: Color,
  difficulty: BotDifficulty = 'beginner',
): GameAction | null {
  const legal = engine.generateLegalActions(state, side);
  if (legal.length === 0) return null;
  if (difficulty === 'beginner') return randomPick(legal);

  const settings = settingsFor(difficulty, state);
  const budget = { nodes: settings.nodes };
  const ranked = orderActions(engine, state, legal, side)
    .slice(0, settings.top)
    .map((action) => {
      const next = engine.applyAction(state, action).state;
      const score = minimax(engine, next, settings.depth, side, -Infinity, Infinity, budget);
      return { action, score: score + actionScore(state, action, side) * 0.1 + Math.random() * settings.noise };
    })
    .sort((a, b) => b.score - a.score);

  return ranked[0]?.action ?? randomPick(legal);
}

function minimax(
  engine: Engine,
  state: GameState,
  depth: number,
  side: Color,
  alpha: number,
  beta: number,
  budget: { nodes: number },
): number {
  budget.nodes -= 1;
  if (budget.nodes <= 0 || depth <= 0 || state.status === 'ended') return evaluate(engine, state, side);

  const active = state.pendingBonus?.side ?? state.turn;
  const legal = orderActions(engine, state, engine.generateLegalActions(state, active), side).slice(0, 12);
  if (legal.length === 0) return evaluate(engine, state, side);
  const maximizing = active === side;

  if (maximizing) {
    let best = -Infinity;
    for (const action of legal) {
      const next = engine.applyAction(state, action).state;
      best = Math.max(best, minimax(engine, next, depth - 1, side, alpha, beta, budget));
      alpha = Math.max(alpha, best);
      if (beta <= alpha || budget.nodes <= 0) break;
    }
    return best;
  }

  let best = Infinity;
  for (const action of legal) {
    const next = engine.applyAction(state, action).state;
    best = Math.min(best, minimax(engine, next, depth - 1, side, alpha, beta, budget));
    beta = Math.min(beta, best);
    if (beta <= alpha || budget.nodes <= 0) break;
  }
  return best;
}

function evaluate(engine: Engine, state: GameState, side: Color): number {
  if (state.status === 'ended') {
    if (state.winner === side) return 100_000;
    if (state.winner === 'draw') return 0;
    return -100_000;
  }

  const enemy = opponentOf(side);
  let score = materialScore(state, side) - materialScore(state, enemy);
  score += (engine.generateLegalMoves(state, side).length - engine.generateLegalMoves(state, enemy).length) * 3;
  score += threatScore(engine, state, side) - threatScore(engine, state, enemy);
  score += storedTempo(state, side) * 60 - storedTempo(state, enemy) * 60;
  if (state.pendingBonus?.side === side) score += 120;
  if (state.pendingBonus?.side === enemy) score -= 120;
  return score;
}

function actionScore(state: GameState, action: GameAction, side: Color): number {
  if (action.kind === 'store_tempo') return 35;
  const move = action.move;
  let score = 0;
  const captured = move.capturedPieceId ? state.pieces[move.capturedPieceId] : undefined;
  if (captured) score += VALUES[captured.type] * 2;
  if (move.promotionTo) score += VALUES[move.promotionTo] - VALUES.pawn;
  if (move.special) score += move.special === 'castle_master' ? 70 : 30;
  const mover = state.pieces[move.pieceId];
  if (mover?.color !== side) score -= 10;
  return score;
}

function orderActions(engine: Engine, state: GameState, actions: GameAction[], side: Color): GameAction[] {
  return [...actions].sort((a, b) => actionScore(state, b, side) - actionScore(state, a, side));
}

function threatScore(engine: Engine, state: GameState, side: Color): number {
  let score = 0;
  for (const move of engine.generateLegalMoves(state, side).slice(0, 80)) {
    const captured = move.capturedPieceId ? state.pieces[move.capturedPieceId] : undefined;
    if (captured) score += VALUES[captured.type] * 0.25;
    if (move.promotionTo) score += 80;
  }
  return score;
}

function materialScore(state: GameState, side: Color): number {
  let score = 0;
  for (const piece of Object.values(state.pieces)) {
    if (piece.color === side) score += VALUES[piece.type];
  }
  return score;
}

function storedTempo(state: GameState, side: Color): number {
  return Number(state.ruleState[`stored-tempo-${side}`] ?? 0);
}

function settingsFor(difficulty: BotDifficulty, state: GameState) {
  if (difficulty !== 'adaptive') return SETTINGS[difficulty];
  const pieceCount = Object.keys(state.pieces).length;
  return pieceCount <= 16 ? { ...SETTINGS.adaptive, depth: 3, nodes: 1_800 } : SETTINGS.adaptive;
}

function opponentOf(side: Color): Color {
  return side === 'white' ? 'black' : 'white';
}

function randomPick<T>(values: T[]): T {
  return values[Math.floor(Math.random() * values.length)]!;
}
